import { ref, computed } from 'vue';

import { defineStore, storeToRefs } from 'pinia';

import {
  getItemFromLocalStorage,
  setItemToLocalStorage,
} from '@/helpers/localStorageServices.js';
import { useAccountStore } from '@/stores/account.js';
import { useBillingStore } from '@/stores/billing.js';

const DISMISSED_STORAGE_KEY = 'dismissed_notifications';
const DISMISS_DURATION_TOKEN_EXPIRING = 24 * 60 * 60; // 24 hours in seconds
const DISMISS_DURATION_TOKEN_REVOKED = 12 * 60 * 60; // 12 hours in seconds
// Trial banner urgency thresholds (in days remaining until trial_end).
// Mirrors the design doc:
//   docs/billing/2026-05-11-trial-lifecycle-UX-phase-a-frontend-design.md
// Low urgency is dismissible for 12h; medium/high stay until the trial advances.
const TRIAL_URGENCY_LOW_DAYS = 7;
const TRIAL_URGENCY_MEDIUM_DAYS = 3;
const DISMISS_DURATION_TRIAL_LOW = 12 * 60 * 60;

export const useConnectionAlertsStore = defineStore('connectionAlerts', () => {
  const connectionNotifications = ref([]);
  // The raw connections list the notifications are derived from, kept so other surfaces (the
  // Overview's "Needs attention" strip) read it without a second request.
  const oauthConnections = ref([]);
  // The business profile `oauthConnections` was fetched for, so a reader can tell the list is
  // not still the previous profile's during a switch.
  const oauthConnectionsBpId = ref(null);
  const isLoaded = ref(false);
  // localStorage isn't reactive — bump this on dismiss so the computed
  // planNotifications (which calls isDismissed) re-evaluates and drops the
  // just-dismissed entry. Connection notifications splice from an array, so
  // they don't need this signal.
  const dismissedTick = ref(0);

  const accountStore = useAccountStore();
  const billingStore = useBillingStore();
  const { currentBusinessProfile } = storeToRefs(accountStore);
  const { trialEndDate, trialDaysRemaining, isDunning, hasInitialized: billingInitialized } =
    storeToRefs(billingStore);

  const getDismissedMap = () => {
    return getItemFromLocalStorage(DISMISSED_STORAGE_KEY) || {};
  };

  const saveDismissedMap = (map) => {
    setItemToLocalStorage(DISMISSED_STORAGE_KEY, map);
  };

  const isDismissed = (eventId) => {
    const map = getDismissedMap();
    const entry = map[eventId];
    if (!entry) return false;

    if (Date.now() >= entry.expiresAt) {
      delete map[eventId];
      saveDismissedMap(map);
      return false;
    }

    return true;
  };

  // Plan / trial notifications — computed so they re-evaluate automatically when
  // currentBusinessProfile.plan.gateState or useBillingStore trial fields change.
  // Connection alerts stay imperative (built once after fetchNotifications).
  const planNotifications = computed(() => {
    // touch dismissedTick so dismiss() invalidates this computed
    void dismissedTick.value;
    const plan = currentBusinessProfile.value?.plan;
    if (!plan) return [];

    const gateState = plan.gateState;
    const result = [];

    // Hard gate used to render nothing here, on the assumption that
    // HardGateDialog covers it. It doesn't cover the ambient case: the dialog is
    // raised by gateGuard only when a WRITE request is blocked (reads are exempt),
    // and the router's hard-gate redirect allowlists /billing. So a hard-gated
    // user who lands on /billing and just reads saw no explanation at all — the
    // one thing they need to understand is why the app is locked. The dialog
    // still explains a blocked *action*; this bar explains the *state*.
    if (gateState === 'hard_gate') {
      const eventId = 'billing_hard_gate';
      if (isDismissed(eventId)) return result;
      result.push({
        event_id: eventId,
        type: 'error',
        message: isDunning.value
          ? "We couldn't process your payment and access is now limited. "
            + '[Update payment method](/billing) to restore it.'
          : 'Your trial and grace period have ended. '
            + '[Subscribe](/billing) to restore access to your account.',
        dismiss_duration: null,
      });
      return result;
    }

    // soft_gate — two distinct causes (webapp#1080 F3): a dunning subscriber
    // (payment failed on an EXISTING subscription) keeps this ambient banner;
    // a trial that ended with no subscription is now a full route block —
    // every page redirects to /billing (router/index.js), where the grace
    // countdown card explains the state, so a banner here would never be seen.
    if (gateState === 'soft_gate') {
      if (!isDunning.value) return result;
      const eventId = 'billing_soft_gate';
      if (isDismissed(eventId)) return result;
      result.push({
        event_id: eventId,
        type: 'error',
        message:
          "We couldn't process your payment. Update your payment method to restore full access. "
          + '[Update payment method](/billing).',
        dismiss_duration: null,
      });
      return result;
    }

    // Active trial — show banner only when ≤ 7 days remain.
    if ((gateState === 'trial_active' || gateState === 'trial_active_pm') && trialEndDate.value) {
      const days = trialDaysRemaining.value;
      if (days > TRIAL_URGENCY_LOW_DAYS) return result;

      // Urgency → banner type (mirrors NotificationBar.vue's --info/--warning/--error)
      let type;
      let dismissDuration;
      if (days > TRIAL_URGENCY_MEDIUM_DAYS) {
        type = 'info';
        dismissDuration = DISMISS_DURATION_TRIAL_LOW;
      } else if (days > 1) {
        type = 'warning';
        dismissDuration = null;
      } else {
        type = 'error';
        dismissDuration = null;
      }

      const eventId = `billing_trial_${type}`;
      if (isDismissed(eventId)) return result;

      const dayWord = days === 1 ? 'day' : 'days';
      // "Add a payment method" is dunning language — it presumes an existing
      // subscription whose card failed. An active trial has no subscription yet,
      // so the ask is to subscribe. (The dunning copy lives in the soft_gate
      // branch above.) `trial_active_pm` already has a card on file, so its
      // "we'll charge you" wording stays accurate.
      const tail = gateState === 'trial_active_pm'
        ? `We'll charge your card on file when the trial ends.`
        : `[Subscribe](/billing) to keep access.`;
      result.push({
        event_id: eventId,
        type,
        message: `Your trial ends in ${days} ${dayWord}. ${tail}`,
        dismiss_duration: dismissDuration,
      });
    }

    return result;
  });

  const notifications = computed(() => [
    ...planNotifications.value,
    ...connectionNotifications.value,
  ]);

  const hasNotifications = computed(() => notifications.value.length > 0);

  const dismissNotification = (eventId) => {
    const notification = notifications.value.find((n) => n.event_id === eventId);
    if (!notification || notification.dismiss_duration == null) return;

    const map = getDismissedMap();
    map[eventId] = {
      expiresAt: Date.now() + notification.dismiss_duration * 1000,
    };
    saveDismissedMap(map);

    // Plan notifications are computed and will exclude this id once we bump
    // dismissedTick (since isDismissed() reads localStorage, which isn't
    // reactive). Connection notifications are an array — splice them directly.
    connectionNotifications.value = connectionNotifications.value.filter(
      (n) => n.event_id !== eventId,
    );
    dismissedTick.value += 1;
  };

  const buildNotificationsFromConnections = (oauthConnections) => {
    const result = [];

    if (!oauthConnections || oauthConnections.length === 0) {
      return result;
    }

    const revokedConnections = [];
    const expiringConnections = [];

    for (const conn of oauthConnections) {
      if (conn.status === 'REVOKED') {
        revokedConnections.push(conn);
        continue;
      }

      // The connections endpoint reports expiry as `tokenExpiryStatus` (VALID | EXPIRES_SOON |
      // EXPIRED), not a timestamp. EXPIRED is not raised here: the token is already dead, and
      // the revoked / reauthorize banners cover the connection once it flips status.
      if (conn.status === 'ACTIVE' && conn.tokenExpiryStatus === 'EXPIRES_SOON') {
        expiringConnections.push(conn);
      }
    }

    // Single notification for all revoked tokens
    if (revokedConnections.length > 0) {
      const eventId = 'token_revoked';
      if (!isDismissed(eventId)) {
        const labels = revokedConnections
          .map((c) => c.amazonApiName || c.amazonApiEmail || 'Unknown')
          .join(', ');
        result.push({
          event_id: eventId,
          type: 'warning',
          message:
            revokedConnections.length === 1
              ? `Your Amazon Ads authentication token (${labels}) need to be refreshed. [Please refresh it now](/settings/connections) to get full power of WisePPC.`
              : `Your Amazon Ads authentication tokens (${labels}) need to be refreshed. [Please refresh them now](/settings/connections) to get full power of WisePPC.`,
          dismiss_duration: DISMISS_DURATION_TOKEN_REVOKED,
        });
      }
    }

    // Single notification for all expiring tokens
    if (expiringConnections.length > 0) {
      const eventId = 'token_expiring';
      if (!isDismissed(eventId)) {
        const labels = expiringConnections
          .map((c) => c.amazonApiName || c.amazonApiEmail || 'Unknown')
          .join(', ');
        result.push({
          event_id: eventId,
          type: 'warning',
          message:
            expiringConnections.length === 1
              ? `Your Amazon Ads authentication token (${labels}) is about to expire. [Please renew it now](/settings/connections) to prevent a disruption in your service.`
              : `Your Amazon Ads authentication tokens (${labels}) are about to expire. [Please renew them now](/settings/connections) to prevent a disruption in your service.`,
          dismiss_duration: DISMISS_DURATION_TOKEN_EXPIRING,
        });
      }
    }

    return result;
  };

  // --- Fetch notifications ---
  // TODO: Replace with dedicated GET /notifications endpoint when available.
  // For now, derive notifications from connections data.

  const fetchNotifications = async () => {
    const requestedBpId = currentBusinessProfile.value?.id ?? null;
    try {
      const data = await accountStore.getBusinessProfileConnections();
      oauthConnections.value = data?.oauthConnections || [];
      oauthConnectionsBpId.value = requestedBpId;
      connectionNotifications.value = buildNotificationsFromConnections(data?.oauthConnections);

      // Plan-based notifications are computed and need billing-store data
      // (trial_end, grace_period_end). Kick off a lazy init so consumers on
      // pages other than /billing still see trial-expiry banners.
      const bpId = currentBusinessProfile.value?.id;
      if (bpId && !billingInitialized.value) {
        // Fire-and-forget — billingStore is reactive and planNotifications
        // will recompute once data arrives.
        billingStore.ensureInitialized(bpId).catch(() => {});
      }
    } catch (error) {
      console.error('[connectionAlerts] Failed to fetch notifications:', error);
      oauthConnections.value = [];
      oauthConnectionsBpId.value = requestedBpId;
      connectionNotifications.value = [];
    } finally {
      isLoaded.value = true;
    }
  };

  return {
    notifications,
    oauthConnections,
    oauthConnectionsBpId,
    isLoaded,
    hasNotifications,
    fetchNotifications,
    dismissNotification,
  };
});
